import React, { JSX } from "react";
import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material";
import { QueryLeafNode } from "system/query/QueryLeafNode";
import SubqueriesKeywordTooltip from "../Tooltips/SubqueriesKeywordTooltip";
import { Query, queryOps } from "system/query/Query";

interface KeywordEntry {
  bucket: any;
  rootBucket: any;
  percentage: number;
  rootPercentage: number;
  absPctDiff: number;
}

export interface SubqueriesKeywordsProps {
  rowResponse: any;
  rootResponse: any;
  setValueDrilldownQuery: (query?: Query) => void;
  setValueDrilldownTitle: (title: string) => void;
}

export default function SubqueriesKeywords(props: SubqueriesKeywordsProps): JSX.Element {
  const theme = useTheme();

  const { rowResponse, rootResponse, setValueDrilldownQuery, setValueDrilldownTitle } = props;

  const [hoveredKeyword, setHoveredKeyword] = React.useState<KeywordEntry | undefined>(); 

  const rowBody = rowResponse.responseBody;
  const rootBody = rootResponse.responseBody;

  const rowTotal = rowBody?._total || 0;
  const rootTotal = rootBody?._total || 0;

  const rootKeywordEntries = rootBody?.keyword?._buckets || [];

  const keywordEntries: KeywordEntry[] | undefined = rootKeywordEntries.map((rootBucket: any) => {
    const bucket = (rowBody?.keyword?._buckets || []).find((b: any) => b._key === rootBucket._key);
    if (!bucket || !rootBucket) { return; }
    const percentage = bucket._count / rowTotal;
    const rootPercentage = rootBucket._count / rootTotal;
    const absPctDiff = Math.abs(percentage - rootPercentage);
    return {
      bucket: bucket,
      rootBucket: rootBucket,
      percentage: percentage,
      rootPercentage: rootPercentage,
      absPctDiff: absPctDiff,
    };
  }).filter(entry => !!entry).sort((a, b) => b.absPctDiff - a.absPctDiff);

  const keywordDisplayRows = keywordEntries?.map((entry => {
    const { bucket, percentage, rootPercentage } = entry;
    const pctDiff = parseFloat(((percentage - rootPercentage) * 100.0).toFixed(1));

    const fieldId = rootBody?.keyword?._field_id;
    const fieldLabel = rootBody?.keyword?._field_label;
    const valueKey = bucket._key;
    const valueLabel = bucket._label;

    let color = theme.palette.text.secondary;
    if (pctDiff > 10) {
      color = theme.colors.namedColors.positiveDark;
    } else if (pctDiff > 1) {
      color = theme.colors.namedColors.positive;
    } else if (pctDiff < -10) {
      color = theme.colors.namedColors.negativeDark;
    } else if (pctDiff < -1) {
      color = theme.colors.namedColors.negative;
    }

    return (
      <Box
        key={bucket._key}
        sx={{ m: 0.3, mx: 0.5, display: "inline-block", backgroundColor: theme.palette.background.default, borderRadius: 1, p: 0.5, px: 1, fontWeight: "bold", color: theme.colors.namedColors.chart1Dark, fontSize: "0.8rem", cursor: "pointer" }}
        onMouseEnter={() => {
          setHoveredKeyword(entry);
        }}
        onMouseLeave={() => {
          setHoveredKeyword(undefined);
        }}
        onClick={() => {
          const drilldownQuery = new Query({ query: { op: queryOps.contains, fields: [fieldId], value: valueKey } as QueryLeafNode});
          setValueDrilldownQuery(drilldownQuery);
          setValueDrilldownTitle(`${fieldLabel}: ${valueLabel}`);
        }}
      >
        {valueLabel}
        <span style={{ color: color, display: "inline-block", marginLeft: "6px" }}>
          {pctDiff > 0 && "+"}
          {pctDiff}%
        </span>
      </Box>
    );
  }));

  return (
  <Box sx={{
      position: "relative",
      p:0, m:0,
      marginTop: "-5px",
      marginBottom: "-5px",
      height: "100%", maxHeight: "75px", minHeight: "60px",
      textAlign: "center",
      overflow: "auto",
    }}>
    <Tooltip title={(hoveredKeyword) ? <SubqueriesKeywordTooltip {...hoveredKeyword} /> : ""}
      open={!!hoveredKeyword}
      followCursor={true}>
      <span>
      {keywordDisplayRows}
      </span>
    </Tooltip>
  </Box>
  );
}